import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail, formatDateIT } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Bell, ArrowClockwise, User, CurrencyEur, Calendar, ArrowRight, ArrowLeft, X } from "@phosphor-icons/react";

const eur = (n) => (n ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function ServicesExpiringPage() {
  useDocumentTitle("Scadenzario");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewDialog, setRenewDialog] = useState({ open: false, service: null });
  const [window, setWindow] = useState(30);
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = { expiring_within: window };
      if (customerFilter && customerFilter !== "all") params.customer_id = customerFilter;
      const { data } = await api.get("/services", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [window, customerFilter]);

  useEffect(() => {
    // load customers once for the filter dropdown
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  // Group by customer
  const grouped = useMemo(() => {
    const m = {};
    for (const s of items) {
      const k = s.customer_id;
      if (!m[k]) m[k] = { customer_id: k, name: `${s.customer_surname || ""} ${s.customer_name || ""}`.trim(), phone: s.customer_phone, services: [] };
      m[k].services.push(s);
    }
    return Object.values(m).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const expiredCount = items.filter((s) => s.is_expired).length;
  const expiringCount = items.length - expiredCount;
  const totalValue = items.reduce((s, it) => s + (it.price || 0), 0);

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="services-expiring-page">
      <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Dashboard
      </Link>

      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// SCADENZARIO</div>
          <h1 className="text-4xl font-display font-bold tracking-tight flex items-center gap-3">
            <Bell size={30} weight="duotone" className="text-primary" />
            Scadenzario
          </h1>
          <p className="text-muted-foreground mt-2">
            Servizi in scadenza nei prossimi <span className="font-mono font-semibold text-foreground">{window} giorni</span> o già scaduti
            {customerFilter !== "all" && customers.find((c) => c.id === customerFilter) && (
              <> · cliente <span className="font-mono font-semibold text-foreground">{(customers.find((c) => c.id === customerFilter) || {}).surname} {(customers.find((c) => c.id === customerFilter) || {}).name}</span></>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Finestra:</Label>
            <div className="flex gap-1 bg-secondary rounded-full p-1">
              {[30, 60, 90].map((n) => (
                <button
                  key={n}
                  onClick={() => setWindow(n)}
                  data-testid={`window-${n}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${window === n ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {n}gg
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 sm:min-w-[260px]">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">Cliente:</Label>
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="h-9" data-testid="customer-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i clienti</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.surname} {c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {customerFilter !== "all" && (
              <Button variant="ghost" size="icon" onClick={() => setCustomerFilter("all")} data-testid="clear-customer-filter" className="rounded-full h-9 w-9">
                <X size={14} />
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="p-4 border-border/70 rounded-lg" data-testid="expiring-summary-total">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Totale</div>
          <div className="text-3xl font-display font-bold mt-1">{items.length}</div>
        </Card>
        <Card className={`p-4 border rounded-lg ${expiredCount > 0 ? "border-rose-500/50 bg-rose-500/5" : "border-border/70"}`} data-testid="expiring-summary-expired">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Scaduti</div>
          <div className={`text-3xl font-display font-bold mt-1 ${expiredCount > 0 ? "text-rose-600 dark:text-rose-400" : ""}`}>{expiredCount}</div>
        </Card>
        <Card className="p-4 border-border/70 rounded-lg" data-testid="expiring-summary-value">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Valore totale</div>
          <div className="text-3xl font-display font-bold mt-1 font-mono">{eur(totalValue)}</div>
        </Card>
      </div>

      {loading && <div className="text-muted-foreground">Caricamento…</div>}
      {!loading && grouped.length === 0 && (
        <Card className="p-12 border-border/70 rounded-lg text-center">
          <div className="text-4xl mb-3">✓</div>
          <div className="text-lg font-display font-bold">Tutto in ordine!</div>
          <div className="text-muted-foreground text-sm">Nessun servizio in scadenza nei prossimi {window} giorni.</div>
        </Card>
      )}

      <div className="space-y-4">
        {grouped.map((g) => (
          <Card key={g.customer_id} className="border-border/70 rounded-lg overflow-hidden" data-testid={`customer-group-${g.customer_id}`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 bg-secondary/40">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-primary/10 text-primary grid place-items-center">
                  <User size={16} weight="duotone" />
                </div>
                <div>
                  <div className="font-medium">{g.name}</div>
                  <div className="font-mono text-xs text-muted-foreground">{g.phone}</div>
                </div>
              </div>
              <Link to={`/clienti/${g.customer_id}`}>
                <Button variant="ghost" size="sm" className="rounded-full gap-1">
                  Scheda cliente <ArrowRight size={13} />
                </Button>
              </Link>
            </div>
            <div className="divide-y divide-border/50">
              {g.services.map((s) => {
                const expired = s.is_expired;
                return (
                  <div key={s.id} className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between px-4 py-3" data-testid={`expiring-service-${s.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium">{s.name}</div>
                        {s.category && (
                          <Badge className="bg-secondary text-foreground border-border hover:bg-secondary font-mono text-[9px] uppercase">{s.category}</Badge>
                        )}
                        {expired ? (
                          <Badge className="bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/40 hover:bg-rose-500/15 font-mono text-[9px] uppercase">
                            Scaduto {Math.abs(s.days_to_expiry)}gg fa
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/40 hover:bg-amber-500/15 font-mono text-[9px] uppercase">
                            Scade in {s.days_to_expiry}gg
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5 flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1"><Calendar size={11} /> {formatDateIT(s.expires_at)}</span>
                        {s.price != null && <span className="inline-flex items-center gap-1"><CurrencyEur size={11} /> {eur(s.price)}/anno</span>}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setRenewDialog({ open: true, service: s })}
                      className="rounded-full gap-1"
                      data-testid={`renew-${s.id}`}
                    >
                      <ArrowClockwise size={14} /> Rinnova
                    </Button>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      <RenewDialog
        open={renewDialog.open}
        onOpenChange={(o) => setRenewDialog({ ...renewDialog, open: o })}
        service={renewDialog.service}
        onDone={() => { setRenewDialog({ open: false, service: null }); load(); }}
      />
    </div>
  );
}

function RenewDialog({ open, onOpenChange, service, onDone }) {
  const [form, setForm] = useState({ new_expires_at: "", price: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !service) return;
    let base = service.expires_at ? new Date(service.expires_at + "T00:00:00") : new Date();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (base < today) base = today;
    base.setFullYear(base.getFullYear() + 1);
    setForm({
      new_expires_at: base.toISOString().slice(0, 10),
      price: service.price ?? "",
      notes: "",
    });
  }, [open, service]);

  const submit = async () => {
    if (!form.new_expires_at) return toast.error("Nuova scadenza richiesta");
    setSaving(true);
    try {
      await api.post(`/services/${service.id}/renew`, {
        new_expires_at: form.new_expires_at,
        price: form.price === "" ? null : parseFloat(form.price),
        notes: form.notes || null,
      });
      toast.success("Servizio rinnovato");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="renew-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowClockwise size={20} weight="duotone" className="text-primary" />
            Rinnova servizio
          </DialogTitle>
          <DialogDescription>
            {service?.name} · scadenza attuale <span className="font-mono">{formatDateIT(service?.expires_at)}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Nuova scadenza</Label>
            <Input type="date" value={form.new_expires_at} onChange={(e) => setForm({ ...form, new_expires_at: e.target.value })} className="mt-1.5" data-testid="renew-date-input" />
            <div className="flex gap-1 mt-2">
              <Button size="sm" variant="outline" onClick={() => setForm({ ...form, new_expires_at: todayPlus(180) })} className="rounded-full text-xs">+6 mesi</Button>
              <Button size="sm" variant="outline" onClick={() => setForm({ ...form, new_expires_at: todayPlus(365) })} className="rounded-full text-xs">+1 anno</Button>
              <Button size="sm" variant="outline" onClick={() => setForm({ ...form, new_expires_at: todayPlus(730) })} className="rounded-full text-xs">+2 anni</Button>
            </div>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Prezzo del rinnovo (€)</Label>
            <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5 font-mono" data-testid="renew-price-input" />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Note (opzionale)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" data-testid="renew-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="renew-submit-btn" className="rounded-full gap-1">
            <ArrowClockwise size={14} /> {saving ? "Rinnovo…" : "Conferma rinnovo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
